import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButtonPure,
  PInputSearch,
  PText,
  type IconName,
} from '@porsche-design-system/components-react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';

import { Popover, type PopoverProps } from './Popover';

const columns = 6;
const rowHeight = 48;

const Picker = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'sm',
    width: '360px',
    maxWidth: 'calc(100vw - 64px)',
  },
});

const ScrollArea = styled('div', {
  base: {
    height: `${rowHeight * 5}px`,
    maxHeight: '40dvh',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollbarGutter: 'stable',
  },
});

const Rows = styled('div', {
  base: {
    position: 'relative',
  },
});

const Row = styled('div', {
  base: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: `${rowHeight}px`,
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    alignItems: 'center',
    justifyItems: 'center',
  },
});

const IconPicker = ({
  value,
  onSelect,
  searchLabel,
}: {
  value?: string;
  onSelect: (icon: IconName) => void;
  searchLabel: string;
}) => {
  const [query, setQuery] = useState('');
  const [focusedIcon, setFocusedIcon] = useState<IconName>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const words = query
    .toLowerCase()
    .trim()
    .split(/[\s-]+/);
  const filteredIcons = icons.filter((icon) =>
    words.every((word) => icon.includes(word)),
  );
  const focusedIndex = focusedIcon ? filteredIcons.indexOf(focusedIcon) : -1;
  // oxlint-disable-next-line react/incompatible-library (Note: will be supported in TanStack virtual v9)
  const virtualizer = useVirtualizer({
    count: Math.ceil(filteredIcons.length / columns),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 1,
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor(range);
      if (focusedIndex >= 0) indexes.push(Math.floor(focusedIndex / columns));
      return [...new Set(indexes)].toSorted((first, second) => first - second);
    },
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (focusedIndex < 0) return;
    const offsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -columns,
      ArrowDown: columns,
      Home: -focusedIndex,
      End: filteredIcons.length - 1 - focusedIndex,
      Tab: event.shiftKey ? -1 : 1,
    };
    const offset = offsets[event.key];
    if (offset === undefined) return;
    const nextIndex = focusedIndex + offset;
    if (
      event.key === 'Tab' &&
      (nextIndex < 0 || nextIndex >= filteredIcons.length)
    ) {
      return;
    }
    event.preventDefault();
    const index = Math.max(0, Math.min(nextIndex, filteredIcons.length - 1));
    const icon = filteredIcons[index];
    if (!icon) return;
    setFocusedIcon(icon);
    virtualizer.scrollToIndex(Math.floor(index / columns), { align: 'auto' });
    requestAnimationFrame(() => {
      const host = scrollRef.current?.querySelector<HTMLElement>(
        `[data-icon="${icon}"]`,
      );
      const button =
        host?.shadowRoot?.querySelector<HTMLButtonElement>('button');
      (button ?? host)?.focus();
    });
  };

  return (
    <Picker>
      <PInputSearch
        name="icon-search"
        label={searchLabel}
        hideLabel
        compact
        indicator
        clear
        autoComplete="off"
        value={query}
        onInput={(event) => {
          setQuery((event.target as HTMLElement & { value: string }).value);
          setFocusedIcon(undefined);
          if (scrollRef.current) scrollRef.current.scrollTop = 0;
          virtualizer.scrollToOffset(0);
        }}
        // Explicit opt out of form association to prevent PDS submit handling
        form=""
      />
      <ScrollArea
        data-testid="icon-select-scroll-area"
        ref={scrollRef}
        onKeyDown={onKeyDown}
        onBlur={(event: FocusEvent<HTMLDivElement>) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setFocusedIcon(undefined);
          }
        }}
      >
        <Rows style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((row) => (
            <Row
              key={row.key}
              style={{ transform: `translateY(${row.start}px)` }}
            >
              {filteredIcons
                .slice(row.index * columns, (row.index + 1) * columns)
                .map((icon) => (
                  <PButtonPure
                    key={icon}
                    data-icon={icon}
                    type="button"
                    icon={icon}
                    active={icon === value}
                    aria={{ 'aria-pressed': icon === value }}
                    onFocus={() => setFocusedIcon(icon)}
                    onClick={() => onSelect(icon)}
                    hideLabel
                  >
                    {icon}
                  </PButtonPure>
                ))}
            </Row>
          ))}
        </Rows>
        <div role="status">
          {filteredIcons.length === 0 && <PText>&ndash;</PText>}
        </div>
      </ScrollArea>
    </Picker>
  );
};

const RequiredInputFix = styled('input', {
  base: {
    opacity: 0,
    height: 0,
    position: 'absolute',
  },
});

export const IconSelect = ({
  onChange,
  value,
  label,
  selectIconLabel,
  searchLabel = 'Search',
  required,
  placement = 'bottom-start',
}: {
  onChange: (value: string) => void;
  value?: string;
  label?: string;
  selectIconLabel?: string;
  searchLabel?: string;
  required?: boolean;
  placement?: PopoverProps['placement'];
}) => {
  const [isVisible, setVisibility] = useState(false);

  return (
    <>
      <PText>
        {label} {required && '*'}
      </PText>
      <Popover
        spacing="normal"
        returnFocus
        target={
          value ? (
            <PButtonPure type="button" icon={value as IconName} hideLabel>
              {selectIconLabel}
            </PButtonPure>
          ) : (
            <PButtonPure type="button">{selectIconLabel}</PButtonPure>
          )
        }
        open={isVisible}
        onChange={setVisibility}
        placement={placement}
      >
        <IconPicker
          value={value}
          searchLabel={searchLabel}
          onSelect={(icon) => {
            onChange(value === icon ? '' : icon);
            setVisibility(false);
          }}
        />
      </Popover>
      {required ? (
        <RequiredInputFix
          tabIndex={-1}
          autoComplete="off"
          value={value}
          onChange={() => {
            // noop
          }}
          required
        />
      ) : null}
    </>
  );
};

const icons: readonly IconName[] = [
  '360',
  '4-wheel-drive',
  'accessibility',
  'active-cabin-ventilation',
  'add',
  'adjust',
  'aggregation',
  'ai-3d-object',
  'ai-chat',
  'ai-code',
  'ai-edit',
  'ai-image',
  'ai-scale',
  'ai-sound',
  'ai-spark',
  'ai-spark-filled',
  'ai-text',
  'ai-video',
  'arrow-compact-down',
  'arrow-compact-left',
  'arrow-compact-right',
  'arrow-compact-up',
  'arrow-double-down',
  'arrow-double-left',
  'arrow-double-right',
  'arrow-double-up',
  'arrow-down',
  'arrow-down-left',
  'arrow-down-right',
  'arrow-first',
  'arrow-head-down',
  'arrow-head-left',
  'arrow-head-right',
  'arrow-head-up',
  'arrow-last',
  'arrow-left',
  'arrow-right',
  'arrow-up',
  'arrow-up-left',
  'arrow-up-right',
  'arrows',
  'attachment',
  'augmented-reality',
  'battery-empty',
  'battery-empty-co2',
  'battery-empty-fuel',
  'battery-full',
  'battery-half',
  'battery-one-quarter',
  'battery-three-quarters',
  'bell',
  'bookmark',
  'bookmark-filled',
  'brain',
  'broadcast',
  'cabriolet',
  'calculator',
  'calendar',
  'camera',
  'car',
  'car-battery',
  'card',
  'charging-active',
  'charging-network',
  'charging-state',
  'charging-station',
  'chart',
  'chat',
  'check',
  'city',
  'climate',
  'climate-control',
  'clock',
  'close',
  'closed-caption',
  'cloud',
  'co2-class',
  'co2-emission',
  'color-picker',
  'compare',
  'compass',
  'configurate',
  'connect-services',
  'copy',
  'country-road',
  'coupe',
  'cubic-capacity',
  'customer-support',
  'cut',
  'delete',
  'disable',
  'dislike',
  'dislike-filled',
  'document',
  'door',
  'download',
  'drag',
  'duration',
  'ear',
  'edit',
  'email',
  'error',
  'error-filled',
  'exclamation',
  'exclamation-filled',
  'external',
  'fast-backward',
  'fast-forward',
  'file-csv',
  'file-excel',
  'filter',
  'fingerprint',
  'flag',
  'flash',
  'fuel-station',
  'garage',
  'genuine-parts',
  'geo-localization',
  'gift',
  'globe',
  'grid',
  'grip',
  'group',
  'hand',
  'heart',
  'heart-filled',
  'highway',
  'highway-filled',
  'history',
  'home',
  'horn',
  'image',
  'increase',
  'information',
  'information-filled',
  'key',
  'laptop',
  'leaf',
  'leather',
  'light',
  'like',
  'like-filled',
  'limousine',
  'linked',
  'list',
  'locate',
  'lock',
  'lock-open',
  'logo-apple-carplay',
  'logo-apple-music',
  'logo-apple-podcast',
  'logo-baidu',
  'logo-delicious',
  'logo-digg',
  'logo-facebook',
  'logo-foursquare',
  'logo-gmail',
  'logo-google',
  'logo-hatena',
  'logo-instagram',
  'logo-kaixin',
  'logo-kakaotalk',
  'logo-kununu',
  'logo-linkedin',
  'logo-naver',
  'logo-pinterest',
  'logo-qq',
  'logo-qq-share',
  'logo-reddit',
  'logo-skyrock',
  'logo-snapchat',
  'logo-sohu',
  'logo-spotify',
  'logo-tecent',
  'logo-telegram',
  'logo-tiktok',
  'logo-tumblr',
  'logo-twitter',
  'logo-viber',
  'logo-vk',
  'logo-wechat',
  'logo-weibo',
  'logo-whatsapp',
  'logo-x',
  'logo-xing',
  'logo-yahoo',
  'logo-youku',
  'logo-youtube',
  'logout',
  'map',
  'menu-dots-horizontal',
  'menu-dots-vertical',
  'menu-lines',
  'microphone',
  'minus',
  'mobile',
  'moon',
  'new-chat',
  'news',
  'north-arrow',
  'oil-can',
  'online-search',
  'parking-brake',
  'parking-light',
  'paste',
  'pause',
  'phone',
  'pin',
  'pin-filled',
  'pivot',
  'play',
  'play-filled',
  'plug',
  'plus',
  'preheating',
  'price-tag',
  'printer',
  'purchase',
  'push-pin',
  'push-pin-off',
  'qr',
  'qr-off',
  'question',
  'question-filled',
  'racing-flag',
  'radar',
  'radio',
  'refresh',
  'replay',
  'reset',
  'return',
  'road',
  'roof-closed',
  'roof-open',
  'route',
  'rss',
  'save',
  'screen',
  'search',
  'seat',
  'send',
  'service-technician',
  'share',
  'shopping-bag',
  'shopping-bag-filled',
  'shopping-cart',
  'shopping-cart-filled',
  'shopping-cart-off',
  'sidebar',
  'sidelights',
  'skip-backward',
  'skip-forward',
  'snowflake',
  'sort',
  'stack',
  'star',
  'star-filled',
  'steering-wheel',
  'stop',
  'stopwatch',
  'subtract',
  'success',
  'success-filled',
  'sun',
  'suv',
  'switch',
  'tablet',
  'tachometer',
  'theme',
  'tire',
  'trigger-finger',
  'truck',
  'turismo',
  'unlinked',
  'upload',
  'user',
  'user-filled',
  'user-group',
  'user-manual',
  'video',
  'view',
  'view-off',
  'volume-off',
  'volume-up',
  'warning',
  'warning-filled',
  'weather',
  'weight',
  'wifi',
  'work',
  'wrench',
  'wrenches',
  'zoom-in',
  'zoom-out',
] as const;
